import { Settings, Bell, LogIn, LogOut } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useSettings } from '../services/SettingsContext';
import { useAuth } from '../services/AuthContext';
import { OrbusInfinityIcon } from '@/components/ui/OrbusInfinityIcon';
import { CopilotLightIcon } from '@/components/copilot/ui/CopilotLightIcon';
import { CopilotIcon } from '@/components/copilot/ui/CopilotIcon';
import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { isCachingEnabled, CACHE_SETTINGS_CHANGED_EVENT } from '@/services/CacheService';

type BannerProps = {
  isCopilotExpanded?: boolean;
  onToggleCopilotExpanded?: () => void;
  isAlertsPanelOpen?: boolean;
  onToggleAlertsPanel?: () => void;
  alertsCount?: number;
};

export function Banner({ isCopilotExpanded, onToggleCopilotExpanded, isAlertsPanelOpen, onToggleAlertsPanel, alertsCount = 0 }: BannerProps) {
  const { apiSettings, setShowModelSelector } = useSettings();
  const { isAuthenticated, isLoading, login, logout } = useAuth();
  const [, setSearchParams] = useSearchParams();
  const [usingCache, setUsingCache] = useState<boolean>(isCachingEnabled);

  useEffect(() => {
    const sync = () => setUsingCache(isCachingEnabled());
    window.addEventListener(CACHE_SETTINGS_CHANGED_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(CACHE_SETTINGS_CHANGED_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);
  
  // Model display text - use label if available, otherwise show generic text
  const modelDisplay = apiSettings.selectedModelLabel || 'No Model Selected';
  
  return (
    <header className="h-12 flex items-center justify-between px-4 shadow-md w-full" style={{ background: 'linear-gradient(90deg, rgb(21, 30, 71) 0%, rgb(38, 74, 112) 100%)' }}>
      <div className="flex items-center gap-3">
        <OrbusInfinityIcon />
        <button
          className="text-white text-sm bg-slate-800 py-1 px-3 rounded-full cursor-pointer hover:opacity-90 transition-opacity"
          onClick={() => setShowModelSelector(true)}
          aria-label="Change model"
        >
          {modelDisplay}
        </button>
        <span className="text-white text-sm bg-slate-800 py-1 px-3 rounded-full">V1.4</span>
        {usingCache && (
          <span className="text-sm font-medium" style={{ color: '#ef4444' }}>You are using cached data!</span>
        )}
      </div>
      
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggleAlertsPanel}
          className="text-white hover:bg-slate-800 hover:text-white relative"
          aria-label={isAlertsPanelOpen ? 'Close notifications' : 'Open notifications'}
          disabled={!onToggleAlertsPanel}
        >
          <Bell className="h-5 w-5" />
          {alertsCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
              {alertsCount > 9 ? '9+' : alertsCount}
            </span>
          )}
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggleCopilotExpanded}
          className={isCopilotExpanded ? "bg-white hover:bg-gray-100" : "text-white hover:bg-slate-800 hover:text-white"}
          aria-label={isCopilotExpanded ? 'Collapse chat' : 'Expand chat'}
          disabled={!onToggleCopilotExpanded}
        >
          <div className="h-5 w-5 flex items-center justify-center">
            {isCopilotExpanded ? <CopilotIcon /> : <CopilotLightIcon />}
          </div>
        </Button>
        {!isLoading && (
          <Button
            variant="ghost"
            size="sm"
            onClick={isAuthenticated ? logout : login}
            className="text-white hover:bg-slate-800 hover:text-white flex items-center gap-1"
            aria-label={isAuthenticated ? 'Sign out' : 'Sign in'}
          >
            {isAuthenticated ? <LogOut className="h-4 w-4" /> : <LogIn className="h-4 w-4" />}
            <span className="text-xs">{isAuthenticated ? 'Sign out' : 'Sign in'}</span>
          </Button>
        )}
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={() => {
            const params = new URLSearchParams(window.location.search);
            params.set('section', 'settings');
            setSearchParams(params, { replace: true });
          }}
          className="text-white hover:bg-slate-800 hover:text-white"
        >
          <Settings className="h-5 w-5" />
        </Button>
      </div>
    </header>
  );
}